import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';

import { actions as reportsActions } from 'features/reports/redux';
import { itemsOnPage } from 'shared/helpers/itemsOnPage';
import convertFormData from './convertFormData';

export default function useRequestHistory() {
  const dispatch = useDispatch();

  const requestHistory = useSelector(state => state.reports.requestHistory);
  const actionProcessing = useSelector(state => state.reports.actionProcessing);
  const { selectedAgentId } = useSelector(state => state.tree);

  const [values, setValues] = useState(() => {
    const date = dayjs().startOf('day');

    return {
      id: '',
      username: '',
      from: date.add(-3, 'day').toDate(),
      to: date.add(1, 'day').toDate(),
      itemsOnPage,
      page: 0,
      type: ['all'],
      name: '',
    };
  });

  const [activeDay, setActiveDay] = useState('');

  const handleTypeChange = type => {
    if (type === 'all') {
      setValues(ps => ({ ...ps, type: ps.type.includes('all') ? [] : ['all'] }));
    } else {
      setValues(ps => {
        const newTypes = ps.type.includes(type)
          ? ps.type.filter(v => v !== type)
          : [...ps.type.filter(v => v !== 'all'), type];

        return { ...ps, type: newTypes };
      });
    }
  };

  const handleValueChange = (name, value) => {
    setValues(ps => ({
      ...ps,
      [name]: value,
    }));

    if (name === 'from' || name === 'to') setActiveDay('');
  };

  const handleChangeActiveDay = dayFilter => {
    setActiveDay(dayFilter);
    setValues(ps => ({
      ...ps,
      from: dayFilter.value,
      to: dayFilter.value2,
    }));
  };

  const pageCount = useMemo(
    () => Math.ceil(requestHistory.count / Number(values.itemsOnPage.find(s => s.active)?.value ?? 10)) ?? 1,
    [values.itemsOnPage],
  );

  const handleSubmit = (e, forcefully) => {
    e.preventDefault();

    if (!actionProcessing || forcefully) {
      dispatch(reportsActions.getRequestHistory(convertFormData(values)));
    }
  };

  useEffect(() => {
    if (selectedAgentId !== null) {
      handleSubmit(new Event('submit'), true);
    }
  }, [values.page, pageCount, values.itemsOnPage, selectedAgentId]);

  useEffect(() => {
    if (values.page > pageCount - 1 && pageCount > 0) {
      handleValueChange('page', 0);
    }
  }, [values.page, pageCount]);

  const activeItemsOnPage = useMemo(() => values.itemsOnPage.find(i => i.active) ?? 10, [values.itemsOnPage]);

  return {
    state: {
      values,
      activeDay,
    },
    output: {
      items: requestHistory.data,
      count: requestHistory.count,
      activeItemsOnPage,
    },
    handlers: {
      setActiveDay,
      handleValueChange,
      handleChangeActiveDay,
      handleTypeChange,
      handleSubmit,
    },
  };
}
